import {Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID} from '@angular/core';
import {Match} from '@shared/models/match';
import {ActivatedRoute, Router} from '@angular/router';
import {SEOService} from '@core/services/seo.service';
import {SharingService} from '@core/services/sharing.service';
import {TeamService} from '@core/services/team.service';
import {LeagueService} from '@core/services/league.service';
import {LinkService} from '@core/services/link.service';
import {isPlatformBrowser, Location} from '@angular/common';
import {MatchDetailViewService} from '@modules/game-detail/services/match-detail-view.service';
import {AppLeague} from '@shared/models/league';
import {GameDetailRoutesMetadata} from '@shared/models/seo/game-detail-routes-metadata';
import {GameDetailRoutes} from '@app/routes/game-detail.routes';
import {PictureSize} from '@app/core/services/picture';
import {LayoutService} from '@utils/services/layout/layout.service';
import {StateService} from '@core/services/state.service';
import {RESPONSE} from '@nguniversal/express-engine/tokens';
import {Response} from 'express';
import {SERVER_REDIRECT_TO} from '@core/server-injection-tokens';
import {AnalyticsHttpService} from '@api/http/analytics/analytics-http.service';
import {AnalyticsViewType} from '@api/models/analytics/analytics-view-type';
import {LeagueType} from '@api/models/league/league-type';
import {AdBreakpoints} from '@lib-modules/ads/models/ad/ads-config';
import {UrlService} from '@utils/services/url/url.service';
import {AdCampaignService} from '@components/ad-campaign/services/ad-campaign.service';
import {EntityType} from '@lib/models/entity/entity-type';
import { Event } from '@api/models/event/event';
import { MatchPollingService, MatchPollUpdateType } from '@lib-modules/match/services/match-polling/match-polling.service';
import { Subscription } from 'rxjs';
import {VideoHeaderContent} from '@app/modules/game-detail/components/match-video-header/match-video-header.component';
import {IStreamingInfo, LivestreamStatus} from '@api/models/livestream/streaming-info';
import {IVideo, VideoSubType} from '@api/models/video/video';
import {INewsPost} from '@api/models/news/news';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';
import {VideoPlayerComponent} from '@components/video-player/components/video-player/video-player.component';

@Component({
  selector: 'app-match-detail-wrapper',
  templateUrl: './match-detail-wrapper.component.html',
  styleUrls: ['./match-detail-wrapper.component.scss']
})
export class MatchDetailWrapperComponent implements OnInit, OnDestroy {

  public eventData: Event;
  public match: Match;
  public videoHeaderContent: VideoHeaderContent;
  public league: AppLeague;
  public imgUrl: string;
  public columnRightBreakpoint = AdBreakpoints.TickerColumnRight;
  public leagueLoading = false;
  public showGamesFromLeague = true;
  public teamId: string;
  private matchId: string;
  private lastPageViewFor: string;
  private poll$: Subscription;

  constructor( @Inject(PLATFORM_ID) protected _platformId: Object,
               @Optional() @Inject(SERVER_REDIRECT_TO) private redirectTo: (url: string, res: Response) => void,
               @Optional() @Inject(RESPONSE) private serverResponse: Response,
               public matchDetailViewService: MatchDetailViewService,
               private matchPollingService: MatchPollingService,
               private layoutService: LayoutService,
               private route: ActivatedRoute,
               private router: Router,
               private seoService: SEOService,
               private sharingService: SharingService,
               private teamService: TeamService,
               private leagueService: LeagueService,
               private location: Location,
               private analyticsHttpService: AnalyticsHttpService,
               private adCampaignService: AdCampaignService ) {}

  public async ngOnInit(): Promise<void> {
    this.layoutService.displayProfileHeader = false;
    this.adCampaignService.setType(EntityType.Event);
    // Match is set in route data resolver
    if (! await this.initMatchData(this.route.snapshot.data.match)) {
      return;
    }
    this.setVideoHeaderContent();
    if (isPlatformBrowser(this._platformId)) {
      this.addPageView();
      this.startPolling();
      // this.matchSocketService.open(this.match, this.teamId);
      // window.onbeforeunload = () => {
      //   this.matchSocketService.close();
      // }
    }
    const currentPath = this.router.url.split('/').pop();
    await this.matchDetailViewService.updateTabs();
    if (!this.matchDetailViewService.showTabs.liveticker && currentPath === GameDetailRoutes.liveTicker) {
      await this.router.navigate([GameDetailRoutes.overview], {relativeTo: this.route, replaceUrl: true});
    }
    this.setPromoCode();
  }

  public ngOnDestroy(): void {
    // this.matchSocketService.close();
    this.stopPolling();
    this.matchDetailViewService.clearMatch();
    this.layoutService.displayProfileHeader = true;
    AnalyticsService.currentEntity = null;
  }

  public onNavClick(): void {
    this.addPageView();
  }

  private addPageView(): void {
    const route = this.router.url.split('/').pop();
    if (this.lastPageViewFor === route) {
      return;
    }
    this.analyticsHttpService.patchAddView(this.match.id, AnalyticsViewType.Event);
    this.lastPageViewFor = route;
  }

  private setTeamId(): boolean {
    const segments = this.router.url.split('/');
    let changeToInApp = true;
    let path = '/';
    const teamIdQueryParam = this.route.snapshot.queryParams['team'];
    const teamIdParam = this.route.snapshot.queryParams['teamid'];
    const teamIdPathParam = this.route.snapshot.params['teamId'];
    const routeEventId = this.route.snapshot.params['id'];

    // Short link or sharing link
    if (teamIdQueryParam) {
      this.teamId = teamIdQueryParam === 'h' ? this.match.teams.home.teamid : this.match.teams.away.teamid;
      path = segments[1];
    } else if (teamIdParam) {
      this.teamId = teamIdParam;
      path = segments[1];
    } else if (teamIdPathParam) { // In-app link
      this.teamId = teamIdPathParam;
      path = segments.pop() || '/';
      changeToInApp = this.teamId === 'heim';
    }

    // Check for old event id
    if (routeEventId && routeEventId.split('-').length > 1) {
      changeToInApp = true;
      path = '/';
    }

    // If no team is set in above parameters, use home team id
    if (!this.teamId || this.teamId === 'heim') {
      this.teamId = this.match.teams.home.teamid;
    }

    // Handle wrong team ids in URL
    if (this.teamId !== this.match.teams.home.teamid && this.teamId !== this.match.teams.away.teamid) {
      changeToInApp = true;
      path = '/';
      this.teamId = this.match.teams.home.teamid;
    }

    // Route to in-app version of match route
    if (changeToInApp) {
      const newPath = LinkService.getLinkToMatch(this.matchId, this.teamId) + '/' + GameDetailRoutes[path];
      if (!StateService.isBrowser) {
        this.redirectTo(newPath, this.serverResponse);
      } else {
        this.location.replaceState(newPath);
        this.router.navigateByUrl(newPath);
      }
    }
    return true;
  }

  private startPolling(): void {
    const poll = this.matchPollingService.add(
      new Event(this.eventData),
      this.teamId,
      [
        MatchPollUpdateType.Status,
        MatchPollUpdateType.Tabs,
        MatchPollUpdateType.Timeline
      ]
    );
    this.poll$ = poll?.updates.subscribe((update) => {
      if (!update) {
        return;
      }
      if (update.type === MatchPollUpdateType.Status) {
        this.matchDetailViewService.updateStatus(update.data);
      }
      if (update.type === MatchPollUpdateType.Tabs) {
        this.matchDetailViewService.updateTabs();
      }
      if (update.type === MatchPollUpdateType.Timeline) {
        this.matchDetailViewService.updateTimeline(update.data);
      }
    });
  }

  private stopPolling(): void {
    this.matchPollingService.remove(this.eventData);
    this.poll$?.unsubscribe();
  }

  private async initMatchData(match: Event): Promise<boolean> {
    this.match = new Match(match);
    AnalyticsService.currentEntity = this.match.getAsEntity();
    this.eventData = match;
    this.matchId = this.match.id;
    if (!this.setTeamId()) {
      return false;
    }
    await this.setMetadata();
    this.matchDetailViewService.setupMatch();
    this.matchDetailViewService.data.currentTeamId = this.teamId;
    this.matchDetailViewService.data.match = this.match;
    this.matchDetailViewService.setSponsors(this.teamId);
    const teams = this.match.teams;
    this.matchDetailViewService.setScores(teams.home.score, teams.away.score);
    this.matchDetailViewService.data.homeTeam = await this.teamService.getTeam(this.match.teams.home.teamid);
    this.matchDetailViewService.data.awayTeam = await this.teamService.getTeam(this.match.teams.away.teamid);
    await this.setLeague();
    this.matchDetailViewService.loadMatchDetails(this.match, this.teamId);
    return true;
  }

  private async setLeague(): Promise<void> {
    try {
      this.leagueLoading = true;
      if (this.match.league_info?.league_type !== LeagueType.League) {
        const data = this.matchDetailViewService.data;
        const current = this.teamId === data.homeTeam._id ? data.homeTeam : data.awayTeam;
        if (current?.leagueids?.length > 0) {
          this.league = await this.leagueService.getLeague(current.leagueids[0]);
        }
        this.showGamesFromLeague = false;
      } else {
        this.league = await this.leagueService.getLeague(this.match.leagueid);
        this.matchDetailViewService.data.league = this.league;
      }
      this.leagueLoading = false;
    } catch (e) {
      this.leagueLoading = false;
      throw e;
    }
  }

  private async setMetadata(): Promise<void> {
    const segments = this.router.url.split('/');
    const path = segments.pop();
    const link = `${UrlService.Web}/${GameDetailRoutes.root.default}/${path?.length ? path : ''}`;
    const canonicalLink = link.replace(':id', this.match.id)
      .replace(':teamId', this.match.teams.home.teamid);
    const ogUrl = link.replace(':id', this.match.id)
      .replace(':teamId', this.teamId);
    const eventTeam = this.match.teams.home.teamid === this.teamId ? this.match.teams.home : this.match.teams.away;
    const isLineupPath = path === GameDetailRoutes.lineup || path === GameDetailRoutes.root.lineupSharing;
    this.imgUrl = eventTeam.getLogoImageUrl(PictureSize.small);
    const metaData = GameDetailRoutesMetadata[path] || GameDetailRoutesMetadata['/'];
    const eventName = this.match.teams.home.name + ' gegen ' + this.match.teams.away.name;
    let sharingData;
    if (eventTeam.formation > -1 && isLineupPath) {
      sharingData = await this.sharingService.fetchEventLineupSharingInfo(this.matchId, this.teamId);
    } else {
      sharingData = await this.sharingService.fetchEventSharingInfo(this.matchId, this.teamId);
    }
    const title = metaData.title.split('{{eventname}}').join(eventName);
    const description = metaData.description.split('{{eventname}}').join(eventName);
    this.seoService.updateCanonicalUrl(canonicalLink);
    this.seoService.setOgUrl(ogUrl);
    this.seoService.setTitle(title);
    this.seoService.setDescription(description);
    this.seoService.setImage(sharingData.image_url);
    this.seoService.setAppLinks(canonicalLink);
  }

  public isLoading(): boolean {
    return !!(this.matchDetailViewService.data && this.matchDetailViewService.data.homeTeam && this.matchDetailViewService.data.awayTeam);
  }

  // Video content
  private setVideoHeaderContent() {
    // Youtube
    this.videoHeaderContent = {
      youtubeLivestream: this.match?.videos?.livestreamURL,
      youtubeHighlights: this.match?.videos.highlightsURL
    }

    // Native videos
    const streamInfo = this.match?.streaming_info;
    if (!streamInfo) {
      return;
    }
    this.videoHeaderContent.eventId = this.matchId;
    switch (streamInfo.live_status) {
      case LivestreamStatus.Live:
        this.handleLiveStream(streamInfo);
        break;
      case LivestreamStatus.Cancelled:
        break;
      default:
        this.handleReplayAndHighlights();
    }
  }

  private handleLiveStream(streamInfo: IStreamingInfo) {
    const livestream = {
      bm_player_url: streamInfo.hls_url,
      preview: streamInfo.livestream_preview,
      paywall: streamInfo.paywall,
      liveStream: true
    } as IVideo;

    if (livestream.bm_player_url) {
      this.videoHeaderContent.livestream = livestream;
    }
  }

  private handleReplayAndHighlights() {
    if (!this.match.videos_native) {
      return;
    }
    const replay = this.match.videos_native.find(video => video.subtype === VideoSubType.Replay);
    const highlights = this.match.videos_native.find(video => video.subtype === VideoSubType.Highlight);

    if (replay || highlights) {
      this.videoHeaderContent.replay = replay;
      this.videoHeaderContent.highlights = highlights;
    }
  }


  private setPromoCode() {
    const promoCode = this.match?.streaming_info?.promo_code;
    if (!promoCode) {
      return;
    }
    VideoPlayerComponent.promoCode = promoCode;
  }
}
